// Open-questions banner — reframe of the previous "conflicts to resolve."
//
// MVP framing (item 1): the dedup pass detects places where the file
// has internal questions a lawyer would ask the homeowner about. The
// homeowner is NOT being asked to adjudicate the truth — they're being
// invited to add context, ask the assistant to investigate, or say "not
// relevant to me."
//
// Three honest verbs, three outcomes:
//
//   - Add answer       → expands an inline note input. Saves the answer
//                        and KEEPS THE CARD visible. The lawyer (or
//                        future case-brief PDF) sees the question + the
//                        homeowner's answer side by side. The homeowner
//                        has provided context, not dismissed.
//
//   - Tell the assistant → opens chat with a prefilled message about
//                          this question. The agent has the
//                          <inconsistencies> system-prompt block and
//                          can call resolveInconsistency once the user
//                          has expressed a position.
//
//   - Skip              → resolves the row with auto-note "skipped —
//                         homeowner says not relevant." Distinct from
//                         "Add answer": skipping = "I don't think this
//                         matters"; answering = "leave it visible
//                         alongside my context for the lawyer."
//
// Layout: cards flow inline with the page (no nested scroll — same
// rule as evidence-gaps). The list is scrollable as part of the page,
// not a separate container.

import { useState } from 'react';
import { m } from '../paraglide/messages';
import {
  type TimelineInconsistency,
  useInconsistencies,
  useResolveInconsistency,
  useSetHomeownerNote,
} from '../timeline';

function kindLabel(k: TimelineInconsistency['kind']): string {
  switch (k) {
    case 'amount':
      return m.openQuestionsKindAmount();
    case 'date':
      return m.openQuestionsKindDate();
    case 'party':
      return m.openQuestionsKindParty();
    case 'status':
      return m.openQuestionsKindStatus();
    default:
      return m.openQuestionsKindOther();
  }
}

function kindIcon(k: TimelineInconsistency['kind']): string {
  switch (k) {
    case 'amount':
      return '€';
    case 'date':
      return '📅';
    case 'party':
      return '👤';
    default:
      return '?';
  }
}

export function InconsistenciesBanner({
  caseId,
  onShowEvidence,
  footnoteByEventId,
}: {
  caseId: string;
  /** Callback fired when "Show evidence" is clicked on a card. The
   *  parent (TimelinePane) is expected to scroll the matching events
   *  into view and pulse-highlight them. */
  onShowEvidence?: (eventIds: string[]) => void;
  /** Map from timeline_events.id → its footnote number, so each card
   *  can render small `¹·³` chips. Optional. */
  footnoteByEventId?: Map<string, number>;
}) {
  const q = useInconsistencies(caseId);
  if (q.isLoading) return null;
  const items = q.data ?? [];
  if (items.length === 0) return null;

  return (
    <section className="inconsistencies-banner" aria-label={m.openQuestionsAria()}>
      <header className="inconsistencies-head">
        <span className="inconsistencies-icon">?</span>
        <span className="inconsistencies-title">
          {items.length === 1
            ? m.openQuestionsTitleOne()
            : m.openQuestionsTitleMany({ count: String(items.length) })}
        </span>
        <span className="inconsistencies-subtitle">{m.openQuestionsSubtitle()}</span>
      </header>
      <ul className="inconsistencies-list">
        {items.map((inc) => (
          <OpenQuestionCard
            key={inc.id}
            caseId={caseId}
            item={inc}
            onShowEvidence={onShowEvidence}
            footnoteByEventId={footnoteByEventId}
          />
        ))}
      </ul>
    </section>
  );
}

function OpenQuestionCard({
  caseId,
  item,
  onShowEvidence,
  footnoteByEventId,
}: {
  caseId: string;
  item: TimelineInconsistency;
  onShowEvidence?: (eventIds: string[]) => void;
  footnoteByEventId?: Map<string, number>;
}) {
  const resolve = useResolveInconsistency(caseId);
  const setNote = useSetHomeownerNote(caseId);
  // Editable buffer for the answer. Initialised from the persisted
  // answer so the user can edit-and-save again without re-typing.
  const [answer, setAnswer] = useState(item.homeowner_note ?? '');
  const [editing, setEditing] = useState(false);
  const pending = resolve.isPending || setNote.isPending;

  const eventIds = (item.event_ids ?? []) as string[];
  const footnoteChips = footnoteByEventId
    ? eventIds
        .map((id) => footnoteByEventId.get(id))
        .filter((n): n is number => typeof n === 'number')
    : [];

  function askAssistant() {
    const desc = item.description.replace(/\s+/g, ' ').trim();
    const text = m.openQuestionsChatPrefill({ desc });
    const ev = new CustomEvent('claimpack:chat-prefill', { detail: { text } });
    window.dispatchEvent(ev);
  }

  function saveAnswer() {
    setNote.mutate({ id: item.id, note: answer.trim() || null });
    setEditing(false);
  }

  function skipQuestion() {
    resolve.mutate({
      id: item.id,
      note: 'skipped — homeowner says not relevant',
    });
  }

  const hasAnswer = !!item.homeowner_note;

  return (
    <li className="inconsistency-card">
      <div className="inconsistency-row">
        <span className={`inconsistency-kind kind-${item.kind}`} aria-label={kindLabel(item.kind)}>
          <span className="inconsistency-kind-icon">{kindIcon(item.kind)}</span>
          {kindLabel(item.kind)}
        </span>
        <p className="inconsistency-description">{item.description}</p>
        {footnoteChips.length > 0 ? (
          <span className="inconsistency-footnotes" aria-label={m.openQuestionsFootnotesAria()}>
            {footnoteChips.map((n, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: footnote numbers can repeat across cards; (n,i) gives stable identity within this card
              <span key={`${n}-${i}`} className="inconsistency-footnote-chip">
                {n}
              </span>
            ))}
          </span>
        ) : null}
      </div>

      {/* Persisted answer — visible alongside the question card so the
       *  user can see what they wrote previously, and the lawyer (or
       *  future PDF render) sees the answer alongside the question. */}
      {hasAnswer && !editing ? (
        <div className="inconsistency-answer small">
          <strong>{m.openQuestionsAnswerLabel()}</strong> {item.homeowner_note}{' '}
          <button
            type="button"
            className="btn-link btn-xs"
            onClick={() => setEditing(true)}
            disabled={pending}
          >
            {m.openQuestionsAnswerEdit()}
          </button>
        </div>
      ) : null}

      <div className="inconsistency-actions">
        {editing ? (
          <>
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={m.openQuestionsAnswerPlaceholder()}
              className="inconsistency-note-input"
              ref={(el) => el?.focus()}
              disabled={pending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !pending) saveAnswer();
                if (e.key === 'Escape') {
                  setEditing(false);
                  setAnswer(item.homeowner_note ?? '');
                }
              }}
            />
            <button
              type="button"
              className="btn-primary btn-xs"
              disabled={pending}
              onClick={saveAnswer}
            >
              {pending ? m.openQuestionsSaving() : m.openQuestionsSaveAnswer()}
            </button>
            <button
              type="button"
              className="btn-ghost btn-xs"
              disabled={pending}
              onClick={() => {
                setEditing(false);
                setAnswer(item.homeowner_note ?? '');
              }}
            >
              {m.openQuestionsCancel()}
            </button>
          </>
        ) : (
          <>
            {!hasAnswer ? (
              <button
                type="button"
                className="btn-primary btn-xs"
                disabled={pending}
                onClick={() => setEditing(true)}
              >
                {m.openQuestionsAddAnswer()}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-ghost btn-xs"
              disabled={pending}
              onClick={askAssistant}
            >
              {m.openQuestionsTellAssistant()}
            </button>
            {eventIds.length > 0 && onShowEvidence ? (
              <button
                type="button"
                className="btn-ghost btn-xs"
                disabled={pending}
                onClick={() => onShowEvidence(eventIds)}
              >
                {m.openQuestionsShowEvidence()}
              </button>
            ) : null}
            <button
              type="button"
              className="btn-link btn-xs inconsistency-dismiss"
              disabled={pending}
              onClick={skipQuestion}
              title={m.openQuestionsSkipTitle()}
            >
              {m.openQuestionsSkip()}
            </button>
          </>
        )}
      </div>
    </li>
  );
}
